import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import EditorFormActions from 'src/common/components/modals/EditorFormActions';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AppDateTimePicker from 'src/common/components/form/AppDateTimePicker';
import { Path, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ObjectSchema, array, boolean, date, number, object, string } from 'yup';

import { toDayjs } from 'src/helpers/dayjs';
import {
  defaultPricingForForm,
  flattenRegularPricing,
} from 'src/helpers/editionFormPricing';
import { translatePriceTier } from 'src/helpers/editionPriceTier';
import { compareTechnicalTypesByDistanceKm } from 'src/helpers/statisticExtras';

import { disableAutofillBg } from 'src/common/components/constants/disableAutofillBg';
import { SelectOption } from 'src/common/components/form/controlled/ControlledSelectField';
import { LanguageKeys } from 'src/common/constants/languages';
import {
  EditionPricingPeriod,
  IAgePriceMatrix,
  IEdition,
  IEditionCompetition,
  IFormEdition,
} from 'src/interfaces/Edition';
import SortableEditionCompetitions from 'src/pages/Dashboard/Editions/forms/SortableEditionCompetitions';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

interface EditionEditorFormProps {
  handleClose?: () => void;
  handleSave: (editedEdition: IFormEdition, id?: string) => void;
  editionToEdit?: IEdition;
}

const PRICE_PERIODS: EditionPricingPeriod[] = ['earlyBird', 'regular', 'atLocation'];

const AGE_PRICE_ROWS: {
  field: keyof IAgePriceMatrix;
  tierKey: 'child' | 'young' | 'discounted' | 'full';
}[] = [
  { field: 'childPrice', tierKey: 'child' },
  { field: 'youngPrice', tierKey: 'young' },
  { field: 'discountedPrice', tierKey: 'discounted' },
  { field: 'fullPrice', tierKey: 'full' },
];

const ageMatrixSchema = (requiredMessage: string) =>
  object({
    childPrice: number().required(requiredMessage),
    youngPrice: number().required(requiredMessage),
    discountedPrice: number().required(requiredMessage),
    fullPrice: number().required(requiredMessage),
  });

const EditionEditorForm = (props: EditionEditorFormProps) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;

  const { handleSave, handleClose, editionToEdit } = props;

  const editionSchema: ObjectSchema<IFormEdition> = object()
    .shape({
      canShowResults: boolean(),
      isSignupOpen: boolean(),
      childPrice: number().required(t('form.yup.genericRequired')),
      competitions: array()
        .of(
          object().shape({
            competitionId: string().required(t('form.yup.genericRequired')),
            distances: array()
              .of(string().required(t('form.yup.genericRequired')))
              .required(t('form.yup.genericRequired')),
          }),
        )
        .default([]),
      date: date().defined().required(t('form.yup.genericRequired')),
      discountedPrice: number().required(t('form.yup.genericRequired')),
      earlyBirdEndsAt: date().defined().required(t('form.yup.genericRequired')),
      fullPrice: number().required(t('form.yup.genericRequired')),
      childMaxAge: number().min(0).required(t('form.yup.genericRequired')),
      groupPrice: number()
        .transform((value, originalValue) =>
          originalValue === '' || originalValue == null || Number.isNaN(value)
            ? undefined
            : value,
        )
        .min(0)
        .optional(),
      pricing: object({
        earlyBird: ageMatrixSchema(t('form.yup.genericRequired')).required(),
        regular: ageMatrixSchema(t('form.yup.genericRequired')).required(),
        atLocation: ageMatrixSchema(t('form.yup.genericRequired')).required(),
      }).required(),
      signupDeadline: date().defined().required(t('form.yup.genericRequired')),
      year: number().required(t('form.yup.genericRequired')),
      youngPrice: number().required(t('form.yup.genericRequired')),
    })
    .required()
    .test('earlyBirdBeforeDeadline', t('editions.errors.earlyBirdAfterDeadline'), values => {
      if (!values?.earlyBirdEndsAt || !values?.signupDeadline) {
        return true;
      }
      return new Date(values.earlyBirdEndsAt).getTime() <= new Date(values.signupDeadline).getTime();
    });

  const pricingDefaults = defaultPricingForForm(editionToEdit);

  const defaultValues = editionToEdit
    ? {
        canShowResults: editionToEdit.canShowResults,
        isSignupOpen: editionToEdit.isSignupOpen,
        childPrice: pricingDefaults.regular.childPrice,
        competitions: editionToEdit.competitions || [],
        date: editionToEdit.date,
        discountedPrice: pricingDefaults.regular.discountedPrice,
        earlyBirdEndsAt: editionToEdit.earlyBirdEndsAt ?? editionToEdit.signupDeadline,
        fullPrice: pricingDefaults.regular.fullPrice,
        childMaxAge: editionToEdit.childMaxAge ?? 6,
        groupPrice: editionToEdit.groupPrice,
        pricing: pricingDefaults,
        signupDeadline: editionToEdit.signupDeadline,
        year: editionToEdit.year,
        youngPrice: pricingDefaults.regular.youngPrice,
      }
    : {
        canShowResults: false,
        isSignupOpen: false,
        competitions: [] as IEditionCompetition[],
        childMaxAge: 6,
        pricing: pricingDefaults,
      };

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm<IFormEdition>({
    resolver: yupResolver(editionSchema),
    defaultValues,
  });

  const {
    fields: competitionFields,
    append,
    move,
    remove,
  } = useFieldArray<IFormEdition>({
    control,
    name: 'competitions',
  });

  const { competitions, distances } = useSystemOptionsStore(
    systemOptionsState => systemOptionsState,
  );

  const systemOptions: SelectOption[] = [];
  const distanceOptions: SelectOption[] = [];

  competitions.forEach(comp =>
    systemOptions.push({
      label: comp.i18n[currentLang],
      value: comp.id,
    }),
  );

  [...distances]
    .sort(compareTechnicalTypesByDistanceKm)
    .forEach(dist =>
      distanceOptions.push({
        label: dist.i18n[currentLang],
        value: dist.id,
      }),
    );

  const onSubmit: SubmitHandler<IFormEdition> = async formData => {
    handleSave(flattenRegularPricing(formData), editionToEdit?.id);
  };

  const dateValue = toDayjs(watch('date'));
  const signupDeadlineValue = toDayjs(watch('signupDeadline'));
  const earlyBirdEndsAtValue = toDayjs(watch('earlyBirdEndsAt'));
  const childMaxAgeValue = watch('childMaxAge') ?? 6;
  const currency = t('base.currency');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 3 }}>
          <TextField
            sx={disableAutofillBg}
            type="number"
            label={t('editions.yearLabel')}
            variant="outlined"
            fullWidth
            margin="dense"
            error={!!errors['year']}
            helperText={errors['year']?.message}
            {...register('year')}
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <AppDateTimePicker
            label={t('editions.dateLabel')}
            value={dateValue}
            onChange={newValue => {
              setValue('date', newValue?.toDate() as Date);
            }}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <AppDateTimePicker
            label={t('editions.earlyBirdEndsAtLabel')}
            ampm={false}
            maxDateTime={signupDeadlineValue ?? dateValue ?? undefined}
            value={earlyBirdEndsAtValue}
            onChange={newValue => {
              setValue('earlyBirdEndsAt', newValue?.toDate() as Date);
            }}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <AppDateTimePicker
            label={t('editions.signupDeadlineLabel')}
            ampm={false}
            maxDateTime={dateValue ?? undefined}
            value={signupDeadlineValue}
            onChange={newValue => {
              setValue('signupDeadline', newValue?.toDate() as Date);
            }}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <FormLabel component="legend" sx={{ mb: 1 }}>
            {t('editions.pricingMatrixLabel', { currency })}
          </FormLabel>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label={t('editions.pricingMatrixLabel', { currency })}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '22%', borderBottom: 0 }} />
                  {PRICE_PERIODS.map(period => (
                    <TableCell
                      key={period}
                      align="center"
                      sx={{ py: 0.5, px: 0.75, fontWeight: 600, borderBottom: 0 }}
                    >
                      {t(`editions.pricingPeriods.${period}`)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {AGE_PRICE_ROWS.map(({ field, tierKey }) => {
                  const tierLabel = translatePriceTier(t, tierKey, childMaxAgeValue);
                  return (
                    <TableRow key={field} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{ py: 0.5, px: 0.75, whiteSpace: 'nowrap' }}
                      >
                        {tierLabel}
                      </TableCell>
                      {PRICE_PERIODS.map(period => {
                        const name = `pricing.${period}.${field}` as Path<IFormEdition>;
                        const fieldError = errors.pricing?.[period]?.[field];
                        const periodLabel = t(`editions.pricingPeriods.${period}`);
                        return (
                          <TableCell key={name} sx={{ py: 0.5, px: 0.75 }}>
                            <TextField
                              sx={disableAutofillBg}
                              type="number"
                              size="small"
                              fullWidth
                              margin="none"
                              error={!!fieldError}
                              slotProps={{
                                htmlInput: {
                                  min: 0,
                                  step: 1,
                                  'aria-label': `${periodLabel} · ${tierLabel}`,
                                },
                                input: {
                                  endAdornment: (
                                    <InputAdornment position="end">{currency}</InputAdornment>
                                  ),
                                },
                              }}
                              {...register(name, { valueAsNumber: true })}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid size={{ xs: 6 }}>
          <TextField
            sx={disableAutofillBg}
            type="number"
            label={t('editions.childMaxAgeLabel')}
            fullWidth
            margin="dense"
            error={!!errors.childMaxAge}
            slotProps={{
              htmlInput: { min: 0, step: 1 },
            }}
            {...register('childMaxAge', { valueAsNumber: true })}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <TextField
            sx={disableAutofillBg}
            type="number"
            label={t('editions.groupPriceLabel')}
            fullWidth
            margin="dense"
            error={!!errors.groupPrice}
            slotProps={{
              htmlInput: { min: 0, step: 1 },
              input: {
                endAdornment: <InputAdornment position="end">{currency}</InputAdornment>,
              },
            }}
            {...register('groupPrice', { valueAsNumber: true })}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <SortableEditionCompetitions
            fields={competitionFields}
            control={control}
            systemOptions={systemOptions}
            distanceOptions={distanceOptions}
            onMove={move}
            onRemove={remove}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() => append({ competitionId: '', distances: [] })}
            startIcon={<AddIcon />}
          >
            {t('editions.appendCompetition')}
          </Button>
        </Grid>

        <Grid size={{ xs: 6 }}>
          <FormControlLabel
            control={
              <Switch
                checked={watch('canShowResults') ?? false}
                onChange={event => setValue('canShowResults', event.target.checked)}
                slotProps={{
                  input: { 'aria-label': t('editions.showResults') },
                }}
              />
            }
            label={t('editions.showResults')}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <FormControlLabel
            control={
              <Switch
                checked={watch('isSignupOpen') ?? false}
                onChange={event => setValue('isSignupOpen', event.target.checked)}
                slotProps={{
                  input: { 'aria-label': t('editions.signupOpen') },
                }}
              />
            }
            label={t('editions.signupOpen')}
          />
        </Grid>

        {errors ? (
          <Grid size={{ xs: 12 }} sx={{ textAlign: 'right' }}>
            {Object.keys(errors).map(key => (
              <FormHelperText key={key} error>
                {t('form.keysWithErrors', { key: t(`form.${key}`) })}
              </FormHelperText>
            ))}
          </Grid>
        ) : null}

        <EditorFormActions onCancel={handleClose} />
      </Grid>
    </form>
  );
};

export default EditionEditorForm;
